/**
 * DviMediaGallery - Responsive grid of DVI media with batch actions
 * 
 * Features:
 * - Stats bar: Total, Ungrouped, Grouped, Excluded counts
 * - Responsive grid (3-col mobile, 4-col desktop)
 * - Selection mode for batch actions
 * - Batch exclude/delete actions
 * - Full-size preview lightbox
 */

import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
} from '@/components/ui/dialog';
import { 
  CheckSquare, 
  Square, 
  EyeOff, 
  Trash2, 
  X,
  Image as ImageIcon,
  Layers,
  AlertCircle,
  Loader2
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { DviMediaCard } from './DviMediaCard';
import type { DviMediaItem } from '@/hooks/useDviMedia';

interface DviMediaGalleryProps {
  media: DviMediaItem[];
  ungroupedCount: number;
  groupedCount: number;
  excludedCount: number;
  onToggleExclude: (mediaId: string, excluded: boolean) => Promise<void>;
  onDelete: (mediaId: string) => Promise<void>;
  canDelete?: boolean;
  isLoading?: boolean;
  isDeleting?: boolean;
  isToggling?: boolean;
  className?: string;
}

export function DviMediaGallery({
  media,
  ungroupedCount,
  groupedCount,
  excludedCount,
  onToggleExclude,
  onDelete,
  canDelete = false,
  isLoading = false,
  isDeleting = false,
  isToggling = false,
  className,
}: DviMediaGalleryProps) {
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [previewMedia, setPreviewMedia] = useState<DviMediaItem | null>(null);
  const [isBatchProcessing, setIsBatchProcessing] = useState(false);

  const totalCount = media.length;
  const activeMedia = media.filter((m) => !m.is_excluded);

  const toggleSelection = (mediaId: string, selected: boolean) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (selected) {
        next.add(mediaId);
      } else {
        next.delete(mediaId);
      }
      return next;
    });
  };

  const selectAll = () => {
    setSelectedIds(new Set(media.map((m) => m.id)));
  };

  const clearSelection = () => {
    setSelectedIds(new Set());
    setSelectionMode(false);
  };

  const handleBatchExclude = async () => {
    if (selectedIds.size === 0) return;
    setIsBatchProcessing(true);
    try {
      for (const id of selectedIds) {
        const item = media.find((m) => m.id === id);
        if (item) {
          await onToggleExclude(id, !item.is_excluded);
        }
      }
      clearSelection();
    } finally {
      setIsBatchProcessing(false);
    }
  };

  const handleBatchDelete = async () => {
    if (selectedIds.size === 0 || !canDelete) return;
    setIsBatchProcessing(true);
    try {
      for (const id of selectedIds) {
        await onDelete(id);
      }
      clearSelection();
    } finally {
      setIsBatchProcessing(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (totalCount === 0) {
    return (
      <div className="text-center py-8 space-y-2">
        <ImageIcon className="h-12 w-12 text-muted-foreground mx-auto" />
        <p className="text-sm text-muted-foreground">No media uploaded yet</p>
        <p className="text-xs text-muted-foreground">Upload photos or videos to get started</p>
      </div>
    );
  }

  return (
    <div className={cn('space-y-3', className)}>
      {/* Stats Bar */}
      <div className="flex items-center gap-2 flex-wrap">
        <Badge variant="secondary" className="gap-1">
          <ImageIcon className="h-3 w-3" />
          {totalCount} total
        </Badge>
        {ungroupedCount > 0 && (
          <Badge variant="outline" className="gap-1 bg-yellow-500/10 text-yellow-600">
            <AlertCircle className="h-3 w-3" />
            {ungroupedCount} ungrouped
          </Badge>
        )}
        {groupedCount > 0 && (
          <Badge variant="outline" className="gap-1 bg-green-500/10 text-green-600">
            <Layers className="h-3 w-3" />
            {groupedCount} grouped
          </Badge>
        )}
        {excludedCount > 0 && (
          <Badge variant="outline" className="gap-1 bg-muted text-muted-foreground">
            <EyeOff className="h-3 w-3" />
            {excludedCount} excluded
          </Badge>
        )}
      </div>

      {/* Selection Mode Toggle + Batch Actions */}
      <div className="flex items-center gap-2 flex-wrap">
        <Button
          variant={selectionMode ? 'secondary' : 'outline'}
          size="sm"
          onClick={() => {
            if (selectionMode) {
              clearSelection();
            } else {
              setSelectionMode(true);
            }
          }}
          className="gap-1.5"
        >
          {selectionMode ? (
            <>
              <X className="h-3 w-3" />
              Cancel
            </>
          ) : (
            <>
              <CheckSquare className="h-3 w-3" />
              Select
            </>
          )}
        </Button>

        {selectionMode && (
          <>
            <Button
              variant="outline"
              size="sm"
              onClick={selectAll}
              className="gap-1.5"
            >
              <Square className="h-3 w-3" />
              All ({media.length})
            </Button>

            {selectedIds.size > 0 && (
              <>
                <span className="text-xs text-muted-foreground">
                  {selectedIds.size} selected
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleBatchExclude}
                  disabled={isBatchProcessing}
                  className="gap-1.5"
                >
                  {isBatchProcessing ? (
                    <Loader2 className="h-3 w-3 animate-spin" />
                  ) : (
                    <EyeOff className="h-3 w-3" />
                  )}
                  Exclude
                </Button>
                {canDelete && (
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={handleBatchDelete}
                    disabled={isBatchProcessing}
                    className="gap-1.5"
                  >
                    {isBatchProcessing ? (
                      <Loader2 className="h-3 w-3 animate-spin" />
                    ) : (
                      <Trash2 className="h-3 w-3" />
                    )}
                    Delete
                  </Button>
                )}
              </>
            )}
          </>
        )}
      </div>

      {/* Media Grid */}
      <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
        {media.map((item) => (
          <DviMediaCard
            key={item.id}
            media={item}
            selected={selectedIds.has(item.id)}
            onSelect={(sel) => toggleSelection(item.id, sel)}
            onToggleExclude={(excluded) => onToggleExclude(item.id, excluded)}
            onDelete={() => onDelete(item.id)}
            onViewFull={() => setPreviewMedia(item)}
            canDelete={canDelete}
            isDeleting={isDeleting}
            isToggling={isToggling}
            selectionMode={selectionMode}
          />
        ))}
      </div>

      {/* Full-size Preview Dialog */}
      <Dialog open={!!previewMedia} onOpenChange={() => setPreviewMedia(null)}>
        <DialogContent className="max-w-4xl p-0 overflow-hidden">
          {previewMedia && (
            previewMedia.media_type === 'video' ? (
              <video 
                src={previewMedia.signedUrl} 
                controls 
                autoPlay
                className="w-full h-auto max-h-[80vh]"
              />
            ) : (
              <img 
                src={previewMedia.signedUrl} 
                alt="DVI media preview" 
                className="w-full h-auto max-h-[80vh] object-contain"
              />
            )
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
